import { Flag, Edit2, X, Check, RefreshCw, WifiOff, Wifi, MapPin, ShieldAlert, AlertTriangle, TrendingUp, TrendingDown, ArrowUpDown } from 'lucide-react';
/**
 * Position Monitor
 *
 * Live grid showing open positions with P&L.
 * Uses WebSocket for real-time updates with HTTP polling fallback.
 */

import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Skeleton } from '../ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { useAuth } from '../../AuthContext';
import { titanApi, ApiError, getWebSocketBase } from '../../lib/api';
import { useEquitiesMode } from '../../hooks/useEquitiesMode';
import { filterStockPositions } from '../../lib/stocksOnly';
import { ClickableTicker } from '../TradingViewModal';

// WebSocket base matches REST (Firebase Hosting proxies /api to Cloud Run; WS uses same host).
const WS_BASE = getWebSocketBase();

// Broker position from Alpaca
interface BrokerPosition {
  symbol: string;
  quantity: number;
  side: string;  // "long" or "short"
  avg_entry_price: number;
  market_value: number;
  unrealized_pnl: number;
  unrealized_pnl_pct: number;
  current_price: number;
  stop_loss_price?: number;
  take_profit_price?: number;
  needs_protection?: boolean;  // True if missing SL or TP (NOT monitored for exit!)
  tracked_by_titan?: boolean;  // True if position is in Titan's position manager
  is_external?: boolean;       // True if opened outside Titan (not correlatable to any bot)
  asset_class?: string;        // stocks, forex, crypto, futures
  broker_type?: string;        // alpaca, oanda, coinbase, tradier
  pattern_name?: string;
  /** Backend taxonomy: titan_ledger | broker_order_tag | decision_log_match | unlinked_import | external_broker_order | … */
  attribution_kind?: string | null;
  bot_id?: string | null;
  entry_intent_id?: string | null;
  tags?: Record<string, string>;
}

// Account info from Alpaca
interface AccountInfo {
  broker: string;
  account_id: string;
  buying_power: number;
  equity: number;
  cash: number;
  positions_value: number;
  is_paper: boolean;
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
};

const formatPercent = (value: number): string => {
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
};

/**
 * TP/SL Proximity Gauge
 * Shows how close the current price is to stop loss or take profit
 */
interface TPSLGaugeProps {
  entryPrice: number;
  currentPrice: number;
  stopLoss?: number;
  takeProfit?: number;
  side: string;
}

const TPSLGauge: React.FC<TPSLGaugeProps> = ({ entryPrice, currentPrice, stopLoss, takeProfit, side }) => {
  if (side === 'cash') {
    return (
      <div title="Cash balance — no stop/target">
        <div className="text-center text-muted-foreground text-sm">
          <span>—</span>
        </div>
      </div>
    );
  }
  // If no TP/SL set, show a simple indicator
  if (!stopLoss && !takeProfit) {
    return (
      <div title="No TP/SL configured">
        <div className="text-center text-muted-foreground text-sm">
          <span>--</span>
        </div>
      </div>
    );
  }

  // Calculate progress toward TP or SL (direction-aware)
  const isShort = side === 'short';
  const slDistance = stopLoss
    ? (isShort ? stopLoss - entryPrice : entryPrice - stopLoss)
    : entryPrice * 0.05; // Default 5% if not set
  const tpDistance = takeProfit
    ? (isShort ? entryPrice - takeProfit : takeProfit - entryPrice)
    : entryPrice * 0.10; // Default 10% if not set

  const currentMove = isShort ? entryPrice - currentPrice : currentPrice - entryPrice;

  // Progress: -100 = at SL, 0 = at entry, +100 = at TP
  let progressPercent = 0;

  if (currentMove >= 0) {
    // Moving toward TP
    progressPercent = tpDistance > 0 ? (currentMove / tpDistance) * 100 : 0;
  } else {
    // Moving toward SL
    progressPercent = slDistance > 0 ? (Math.abs(currentMove) / slDistance) * 100 : 0;
  }

  // Clamp progress to 0 to 100
  progressPercent = Math.min(100, progressPercent);

  const isProfit = currentMove >= 0;
  const isCritical = progressPercent > 80;

  // Determine label
  let label = '';
  if (isProfit && takeProfit) {
    label = `${progressPercent.toFixed(0)}% to TP`;
  } else if (!isProfit && stopLoss) {
    label = `${progressPercent.toFixed(0)}% to SL`;
  } else if (isProfit) {
    label = `+${progressPercent.toFixed(0)}%`;
  } else {
    label = `-${progressPercent.toFixed(0)}%`;
  }

  // Color based on direction and proximity
  const getColor = () => {
    if (isProfit) {
      return isCritical ? 'bg-emerald-500' : 'bg-emerald-400';
    } else {
      return isCritical ? 'bg-rose-500' : 'bg-rose-400';
    }
  };

  const tooltipText = [
    `Entry: ${formatCurrency(entryPrice)}`,
    `Current: ${formatCurrency(currentPrice)} (${currentMove >= 0 ? '+' : ''}${((currentMove / entryPrice) * 100).toFixed(2)}%)`,
    stopLoss ? `Stop Loss: ${formatCurrency(stopLoss)} (${(Math.abs(slDistance) / entryPrice * 100).toFixed(1)}% away)` : null,
    takeProfit ? `Take Profit: ${formatCurrency(takeProfit)} (${(Math.abs(tpDistance) / entryPrice * 100).toFixed(1)}% away)` : null,
  ].filter(Boolean).join('\n');

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-2">
            {/* Gauge container */}
            <div className="relative w-20 h-2 bg-muted rounded-full overflow-hidden">
              {/* Progress fill */}
              <div
                className={`absolute top-0 left-0 h-full rounded-full transition-all ${getColor()}`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            {/* Label */}
            <span className={`text-xs font-medium whitespace-nowrap ${isProfit ? 'text-emerald-500' : 'text-rose-500'}`}>
              {label}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="text-xs space-y-0.5 whitespace-pre-line">{tooltipText}</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * Risk Indicator Component
 * Shows risk badges for positions that need attention
 */
interface RiskIndicatorProps {
  position: BrokerPosition;
}

const RiskIndicator: React.FC<RiskIndicatorProps> = ({ position }) => {
  const badges: React.ReactNode[] = [];

  // Check if position is at risk (P&L < -3%)
  const atRisk = position.unrealized_pnl_pct < -3;

  // Check if near stop loss -- direction-aware
  // "Near SL" means price has moved >60% of the way from entry toward stop loss
  const isShort = position.side === 'short';
  const slDistance = position.stop_loss_price ? (isShort
    ? (position.stop_loss_price - position.current_price) / position.current_price * 100
    : (position.current_price - position.stop_loss_price) / position.current_price * 100
  ) : 999;
  const slRange = position.stop_loss_price ? Math.abs(position.avg_entry_price - position.stop_loss_price) : 0;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const slProgress = slRange > 0 ? 1 - (slDistance * position.current_price / 100) / slRange : 0;
  const nearSL = position.stop_loss_price && slDistance > 0 && slDistance < 1.0;

  // Check if near take profit -- direction-aware
  // "Near TP" means price has moved >80% of the way from entry toward take profit
  const tpDistance = position.take_profit_price ? (isShort
    ? (position.current_price - position.take_profit_price) / position.current_price * 100
    : (position.take_profit_price - position.current_price) / position.current_price * 100
  ) : 999;
  const nearTP = position.take_profit_price && tpDistance > 0 && tpDistance < 1.0;

  // Check if no protection set - use the API field if available, otherwise check locally
  const noProtection = position.needs_protection ?? (!position.stop_loss_price || !position.take_profit_price);

  // UNPROTECTED is the most critical - show it first with high visibility
  if (noProtection) {
    badges.push(
      <span
        key="no-protection"
        title="CRITICAL: No SL/TP protection! Exits will NOT trigger automatically. Click 'Protect All' to add protection."
        className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[0.65rem] font-bold bg-rose-500/15 text-rose-600 animate-pulse"
      >
        <AlertTriangle className="w-3 h-3" />
        UNPROTECTED
      </span>
    );
  }

  if (atRisk) {
    badges.push(
      <span
        key="at-risk"
        title="Position is at risk (P&L below -3%)"
        className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[0.65rem] font-semibold bg-amber-500/15 text-amber-600"
      >
        <AlertTriangle className="w-3 h-3" />
        At Risk
      </span>
    );
  }

  if (nearSL) {
    badges.push(
      <span
        key="near-sl"
        title="Price is within 2% of stop loss"
        className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[0.65rem] font-semibold bg-rose-500/15 text-rose-600"
      >
        <Flag className="w-3 h-3" />
        Near SL
      </span>
    );
  }

  if (nearTP) {
    badges.push(
      <span
        key="near-tp"
        title="Price is within 2% of take profit"
        className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[0.65rem] font-semibold bg-emerald-500/15 text-emerald-600"
      >
        <Flag className="w-3 h-3" />
        Near TP
      </span>
    );
  }

  if (badges.length === 0) {
    return (
      <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[0.65rem] font-semibold bg-emerald-500/10 text-emerald-600">
        OK
      </span>
    );
  }

  return (
    <div className="flex flex-wrap gap-1">
      {badges}
    </div>
  );
};

type SortField = 'symbol' | 'unrealized_pnl' | 'unrealized_pnl_pct' | 'market_value';
type SortDirection = 'asc' | 'desc';

// Editable cell state type
interface EditingCell {
  symbol: string;
  field: 'stop_loss' | 'take_profit';
}

const PositionMonitor: React.FC = () => {
  const { equities_only: equitiesOnly } = useEquitiesMode();
  const [positions, setPositions] = useState<BrokerPosition[]>([]);
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [isPaper, setIsPaper] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Informational (non-error) status, e.g. "close order resting — fills at open"
  const [notice, setNotice] = useState<string | null>(null);
  const [liquidating, setLiquidating] = useState<string | null>(null);
  const [liquidatingAll, setLiquidatingAll] = useState(false);
  const [settingTPSL, setSettingTPSL] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [wsConnected, setWsConnected] = useState(false);
  const { getToken, currentUser } = useAuth();

  // Inline editing state
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [savingEdit, setSavingEdit] = useState(false);

  // WebSocket refs - using refs instead of state to avoid re-render loops
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const cachedTokenRef = useRef<string | null>(null);
  const tokenRefreshNeededRef = useRef<boolean>(true);
  const wsReconnectAttemptRef = useRef(0);  // Use ref to avoid useEffect re-runs
  const connectWebSocketRef = useRef<(() => Promise<void>) | null>(null);
  const maxReconnectAttempts = 6;
  const baseReconnectDelay = 20000; // 20s — edge rate limits often trigger on WS upgrade storms

  // Sorting state - default to P&L descending (best performers first)
  const [sortField, setSortField] = useState<SortField>('unrealized_pnl');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Sort positions
  const sortedPositions = useMemo(() => {
    const sorted = [...positions].sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (sortField) {
        case 'symbol':
          aVal = a.symbol;
          bVal = b.symbol;
          break;
        case 'unrealized_pnl':
          aVal = a.unrealized_pnl;
          bVal = b.unrealized_pnl;
          break;
        case 'unrealized_pnl_pct':
          aVal = a.unrealized_pnl_pct;
          bVal = b.unrealized_pnl_pct;
          break;
        case 'market_value':
          aVal = a.market_value;
          bVal = b.market_value;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [positions, sortField, sortDirection]);

  // Paginate positions
  const paginatedPositions = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedPositions.slice(start, start + rowsPerPage);
  }, [sortedPositions, page, rowsPerPage]);

  const totalPages = Math.ceil(sortedPositions.length / rowsPerPage);

  const applyPositions = (raw: BrokerPosition[]) => {
    setPositions(filterStockPositions(raw, equitiesOnly));
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const fetchPositions = async () => {
    setLoading(true);
    try {
      const res = await titanApi.positions() as any;
      // The API client 'get' returns the body directly.
      // If the backend returns { data: { positions, account, paper } }, then:
      if (res && res.data) {
        applyPositions(res.data.positions || []);
        setAccount(res.data.account || null);
        setIsPaper(res.data.paper ?? true);
      } else if (Array.isArray(res)) {
        applyPositions(res);
      } else if (res && res.positions) {
        applyPositions(res.positions || []);
        setAccount(res.account || null);
        setIsPaper(res.paper ?? true);
      }
      setError(null);
    } catch (err) {
      console.error('Failed to fetch positions:', err);
      setError(err instanceof ApiError ? err.detail : 'Failed to fetch positions');
    } finally {
      setLoading(false);
    }
  };

  // WebSocket connection with exponential backoff reconnection
  const connectWebSocket = async () => {
    // Don't reconnect if we've exceeded max attempts
    if (wsReconnectAttemptRef.current >= maxReconnectAttempts) {
      console.warn('Max WebSocket reconnect attempts reached, falling back to HTTP polling');
      return;
    }

    try {
      // Only refresh token when needed (first connect or after auth failure)
      let token = cachedTokenRef.current;
      if (!token || tokenRefreshNeededRef.current) {
        token = await getToken();
        if (token) {
          cachedTokenRef.current = token;
          tokenRefreshNeededRef.current = false;
        }
      }

      if (!token) {
        console.warn('No auth token, cannot connect WebSocket');
        return;
      }

      // Close existing connection if any
      if (wsRef.current) {
        wsRef.current.close();
      }

      const wsUrl = `${WS_BASE}/api/titan/ws/positions`;
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        // Send auth token as first message (avoids exposing token in URL/logs)
        ws.send(JSON.stringify({ type: 'auth', token }));
        console.log('Position WebSocket connected');
        setWsConnected(true);
        wsReconnectAttemptRef.current = 0;
        setError(null);

        // Start ping interval to keep connection alive
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, 25000);
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          if (message.type === 'positions') {
            const data = message.data;
            applyPositions(data.positions || []);
            setAccount(data.account || null);
            setIsPaper(data.paper ?? true);
            setLoading(false);

            if (data.error) {
              setError(data.error?.message || (typeof data.error === 'string' ? data.error : 'An error occurred'));
            } else {
              setError(null);
            }
          } else if (message.type === 'pong') {
            // Ping response received, connection is healthy
          } else if (message.type === 'heartbeat') {
            // Server heartbeat received
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };

      ws.onerror = () => {
        // WS errors are expected on Cloud Run — silent fallback to HTTP polling
        setWsConnected(false);
        tokenRefreshNeededRef.current = true;
      };

      ws.onclose = (event) => {
        console.log('Position WebSocket closed:', event.code, event.reason);
        setWsConnected(false);

        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        // Auth errors — refresh token then reconnect.
        // Previously: 4003 set tokenRefreshNeededRef=true but was excluded from
        // the reconnect block below, so the connection never recovered after a
        // Firebase ID token expiry (tokens expire after 1 hour).
        if (event.code === 4001 || event.code === 4003) {
          console.log('WebSocket auth error — refreshing token and reconnecting');
          tokenRefreshNeededRef.current = true;
          cachedTokenRef.current = null;
          // Short delay to allow token refresh, then reconnect
          const attempt = wsReconnectAttemptRef.current;
          if (attempt < maxReconnectAttempts) {
            wsReconnectAttemptRef.current = attempt + 1;
            reconnectTimeoutRef.current = setTimeout(() => {
              connectWebSocketRef.current?.();
            }, 3000); // 3s to give Firebase time to mint a fresh token
          }
          return;
        }

        // Reconnect with exponential backoff for non-auth closes
        if (event.code !== 1000) {
          const attempt = wsReconnectAttemptRef.current;
          if (attempt < maxReconnectAttempts) {
            const delay = Math.min(baseReconnectDelay * Math.pow(2, attempt), 120000);
            console.log(`Reconnecting in ${delay}ms (attempt ${attempt + 1}/${maxReconnectAttempts})`);
            wsReconnectAttemptRef.current = attempt + 1;
            reconnectTimeoutRef.current = setTimeout(() => {
              connectWebSocketRef.current?.();
            }, delay);
          } else {
            console.log(`WebSocket reconnect limit reached (${maxReconnectAttempts}). Using HTTP polling.`);
          }
        }
      };

      wsRef.current = ws;
    } catch (e) {
      console.error('Failed to connect WebSocket:', e);
      setWsConnected(false);

      // Check if it's a quota error - if so, wait longer and don't refresh token
      const isQuotaError = e instanceof Error && e.message.includes('quota');
      if (isQuotaError) {
        console.warn('Firebase quota exceeded, waiting before retry...');
        const quotaDelay = 60000;
        wsReconnectAttemptRef.current += 1;
        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocketRef.current?.();
        }, quotaDelay);
      } else {
        tokenRefreshNeededRef.current = true;
      }
    }
  };

  // Store the function in a ref so it can be called from within itself (for reconnects)
  connectWebSocketRef.current = connectWebSocket;

  useEffect(() => {
    const init = async () => {
      await fetchPositions();
      setTimeout(() => connectWebSocketRef.current?.(), 4000);
    };
    init();

    // Crash-resilience fallback (replaces the former Firestore onSnapshot —
    // no Google dependency). Whenever the WebSocket is NOT connected, poll the
    // REST positions endpoint every 10s so the UI keeps updating even if the
    // live tick stream drops. WebSocket remains the fastest/authoritative path
    // when connected, so we skip the poll while it's open.
    const pollTimer: ReturnType<typeof setInterval> = setInterval(() => {
      const ws = wsRef.current;
      const isWsActive = ws && ws.readyState === WebSocket.OPEN;
      if (!isWsActive) {
        fetchPositions().catch(() => { /* transient — next tick retries */ });
      }
    }, 10000);

    return () => {
      clearInterval(pollTimer);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const liquidatePosition = async (symbol: string) => {
    if (!window.confirm(`Are you sure you want to liquidate your ${symbol} position? This will sell all shares at market price.`)) {
      return;
    }

    setLiquidating(symbol);
    setNotice(null);
    try {
      const res = await titanApi.liquidate(symbol);
      // A resting/queued close is NOT an instant close — say so instead of
      // silently refetching (the position will still be visible until fill).
      if (res && res.filled === false && res.message) {
        setNotice(res.message);
      }
      await fetchPositions();
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Failed to liquidate position');
    } finally {
      setLiquidating(null);
    }
  };

  const liquidateAllPositions = async () => {
    if (!window.confirm(`Are you sure you want to LIQUIDATE ALL ${positions.length} positions? This cannot be undone.`)) {
      return;
    }

    setLiquidatingAll(true);
    setNotice(null);
    try {
      const res = await titanApi.liquidateAll();
      if (res && res.pending_market_open && res.message) {
        setNotice(res.message);
      }
      await fetchPositions();
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Failed to liquidate all positions');
    } finally {
      setLiquidatingAll(false);
    }
  };

  const [reconciling, setReconciling] = useState(false);
  const [reconcilingFull, setReconcilingFull] = useState(false);

  const reconcilePositionsFull = async () => {
    if (!window.confirm(
      'Full broker reconciliation?\n\n' +
      '• Closes Titan ghosts (not at Alpaca)\n' +
      '• Imports broker-only positions as External (excluded from bot KPIs)\n' +
      '• Re-labels matched positions when attribution can be inferred'
    )) {
      return;
    }
    setReconcilingFull(true);
    setError(null);
    try {
      const body = await titanApi.reconcileFull() as Record<string, unknown>;
      setSuccessMessage(
        `Full reconcile: broker ${(body.broker_symbols as string[])?.length ?? 0} · ` +
        `ghosts closed ${body.ghosts_closed ?? 0} · ` +
        `external imported ${body.external_imported ?? 0} · ` +
        `matched ${body.managed_matched ?? 0} · ` +
        `relabeled ${(body.relabeled as unknown[])?.length ?? 0}.`,
      );
      await fetchPositions();
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Full reconcile failed');
    } finally {
      setReconcilingFull(false);
    }
  };

  const reconcilePositions = async () => {
    if (!window.confirm(
      'Reconcile Titan positions with the broker?\n\n' +
      'Any position Titan is tracking that does NOT exist at Alpaca will be marked closed.\n' +
      'Use this when Titan shows ghost positions that no longer exist at the broker.'
    )) {
      return;
    }
    setReconciling(true);
    setError(null);
    try {
      const body = await titanApi.reconcile() as any;
      setSuccessMessage(
        `Reconciled: broker holds ${body?.broker_symbols?.length ?? 0} position(s). ` +
        `Closed ${body?.orphans_closed ?? 0} orphan(s). ` +
        `Remaining: ${body?.titan_open_after ?? 0}. ` +
        `Labels repaired: ${body?.labels_repaired ?? 0}.`,
      );
      await fetchPositions();
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Reconcile failed');
    } finally {
      setReconciling(false);
    }
  };

  // Count unprotected positions
  const unprotectedCount = useMemo(() => {
    return positions.filter(p => p.needs_protection ?? (!p.stop_loss_price || !p.take_profit_price)).length;
  }, [positions]);

  const setAllTPSL = async () => {
    const slPct = 5;
    const tpPct = 10;

    const unprotectedSymbols = positions
      .filter(p => p.needs_protection ?? (!p.stop_loss_price || !p.take_profit_price))
      .map(p => p.symbol);

    if (unprotectedSymbols.length === 0) {
      setSuccessMessage('All positions are already protected with SL/TP');
      return;
    }

    if (!window.confirm(
      `Protect ${unprotectedSymbols.length} unprotected positions?\n\n` +
      `Stop Loss: ${slPct}%\n` +
      `Take Profit: ${tpPct}%\n\n` +
      `Symbols: ${unprotectedSymbols.join(', ')}\n\n` +
      `This will:\n` +
      `- Calculate SL/TP from entry price\n` +
      `- Save values to Firestore (persist across restarts)\n` +
      `- Add positions to exit monitoring\n\n` +
      `The controller must be running.`
    )) {
      return;
    }

    setSettingTPSL(true);
    setError(null);
    try {
      const result = await titanApi.protectAll(slPct, tpPct);
      setSuccessMessage(
        `Protected ${result.protected_count} positions with ${slPct}% SL / ${tpPct}% TP. ` +
        `Exits are now monitored.`
      );

      await fetchPositions();
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Failed to protect positions');
    } finally {
      setSettingTPSL(false);
    }
  };

  // Start editing a cell
  const startEditing = (symbol: string, field: 'stop_loss' | 'take_profit', currentValue: number | undefined) => {
    setEditingCell({ symbol, field });
    setEditValue(currentValue ? currentValue.toFixed(2) : '');
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingCell(null);
    setEditValue('');
  };

  // Save edit to backend
  const saveEdit = async () => {
    if (!editingCell) return;

    const numValue = parseFloat(editValue);
    if (isNaN(numValue) || numValue <= 0) {
      setError('Please enter a valid price greater than 0');
      return;
    }

    setSavingEdit(true);
    setError(null);

    try {
      const params: any = {};
      if (editingCell.field === 'stop_loss') {
        params.stop_loss_price = numValue.toFixed(2);
      } else {
        params.take_profit_price = numValue.toFixed(2);
      }

      const result = await titanApi.updateExitPrice(editingCell.symbol, params);

      // Optimistic update
      setPositions(prev => prev.map(pos => {
        if (pos.symbol === editingCell.symbol) {
          return {
            ...pos,
            stop_loss_price: result.stop_loss_price ?? pos.stop_loss_price,
            take_profit_price: result.take_profit_price ?? pos.take_profit_price,
          };
        }
        return pos;
      }));

      setSuccessMessage(
        `${editingCell.field === 'stop_loss' ? 'Stop Loss' : 'Take Profit'} updated to $${numValue.toFixed(2)} for ${editingCell.symbol}`
      );

      setEditingCell(null);
      setEditValue('');

    } catch (e) {
      setError(e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Failed to update exit price');
    } finally {
      setSavingEdit(false);
    }
  };

  // Handle Enter key to save, Escape to cancel
  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEdit();
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  const totalPnl = positions.reduce((sum, p) => sum + p.unrealized_pnl, 0);
  const totalValue = positions.reduce((sum, p) => sum + p.market_value, 0);

  const SortableHeader: React.FC<{ field: SortField; children: React.ReactNode; className?: string }> = ({ field, children, className }) => (
    <TableHead className={className}>
      <button
        className="flex items-center gap-1 hover:text-foreground transition-colors"
        onClick={() => handleSort(field)}
      >
        {children}
        <ArrowUpDown className={`w-3 h-3 ${sortField === field ? 'opacity-100' : 'opacity-40'}`} />
      </button>
    </TableHead>
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="rounded-lg border bg-card p-3 md:p-4">
        <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            <span className="font-semibold">Broker Positions</span>
            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-muted">{positions.length}</span>
            {!loading && (
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${isPaper ? 'bg-amber-500/10 text-amber-700' : 'bg-rose-500/10 text-rose-700'}`}>
                {isPaper ? 'Paper' : 'Live'}
              </span>
            )}
            <span
              title={wsConnected ? 'Real-time WebSocket connected' : 'WebSocket disconnected, using polling'}
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${wsConnected ? 'bg-emerald-500/10 text-emerald-600' : 'bg-zinc-500/10 text-zinc-500'}`}
            >
              {wsConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
              {wsConnected ? 'Live' : 'Polling'}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {account && (
              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-blue-500/10 text-blue-700">
                {`Equity: ${formatCurrency(account.equity)}`}
              </span>
            )}
            {positions.length > 0 && (
              <>
                <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-muted">
                  {`Value: ${formatCurrency(totalValue)}`}
                </span>
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${totalPnl >= 0 ? 'bg-emerald-500/10 text-emerald-600' : 'bg-rose-500/10 text-rose-600'}`}>
                  {totalPnl >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {`P&L: ${formatCurrency(totalPnl)}`}
                </span>
              </>
            )}
            {positions.length > 0 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={setAllTPSL}
                  disabled={settingTPSL || unprotectedCount === 0}
                  className={unprotectedCount > 0 ? 'border-amber-500 text-amber-600 hover:bg-amber-500/10' : ''}
                  title={
                    unprotectedCount > 0
                      ? `CRITICAL: ${unprotectedCount} positions need protection! Click to add 5% SL / 10% TP.`
                      : "All positions are protected with SL/TP"
                  }
                >
                  <ShieldAlert className="w-4 h-4 mr-1" />
                  {settingTPSL
                    ? 'Protecting...'
                    : unprotectedCount > 0
                      ? `Protect (${unprotectedCount})`
                      : 'All Protected'
                  }
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={reconcilePositions}
                  disabled={reconciling || reconcilingFull}
                  title="Close Titan ghosts not held at the broker"
                >
                  {reconciling ? 'Reconciling...' : 'Close ghosts'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={reconcilePositionsFull}
                  disabled={reconciling || reconcilingFull}
                  title="Full reconcile: close ghosts, import external broker positions, relabel attribution"
                >
                  {reconcilingFull ? 'Full sync...' : 'Full reconcile'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={liquidateAllPositions}
                  disabled={liquidatingAll}
                  className="border-rose-500 text-rose-600 hover:bg-rose-500/10"
                  title="Liquidate all positions at market price"
                >
                  {liquidatingAll ? 'Liquidating...' : 'Liquidate All'}
                </Button>
              </>
            )}
            <Button variant="ghost" size="icon" onClick={fetchPositions} title="Refresh">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>
            {error}
            {error.includes('credentials') && (
              <span className="block text-xs mt-1">
                Configure Alpaca under Settings → Broker, or System Config Hub → Integrations (Alpaca uses the broker credential store).
              </span>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Informational notice (e.g. close order resting until the open) */}
      {notice && (
        <Alert className="border-sky-500/50 bg-sky-500/10">
          <AlertDescription>{notice}</AlertDescription>
        </Alert>
      )}

      {/* CRITICAL: Unprotected Positions Warning */}
      {!loading && unprotectedCount > 0 && (
        <Alert variant="destructive" className="border-amber-500/50 bg-amber-500/10 text-amber-800">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>
            {unprotectedCount} position{unprotectedCount > 1 ? 's have' : ' has'} NO SL/TP protection!
          </AlertTitle>
          <AlertDescription className="flex items-center justify-between">
            <span>
              These positions are NOT monitored for exits. Stop losses and take profits will NOT trigger automatically.
              Click "Protect All" to add 5% SL / 10% TP protection and enable exit monitoring.
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={setAllTPSL}
              disabled={settingTPSL}
              className="ml-3 shrink-0 border-amber-500 text-amber-700 hover:bg-amber-500/10"
            >
              {settingTPSL ? 'Protecting...' : `Protect All (${unprotectedCount})`}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* No Account Warning */}
      {!loading && !error && !account && (
        <Alert>
          <AlertDescription>
            No broker account connected. Add Alpaca keys in Settings → Broker or System Config Hub → Integrations.
          </AlertDescription>
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <div className="space-y-3 p-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && positions.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <MapPin className="w-12 h-12 text-muted-foreground/30 mb-3" />
          <span className="text-lg font-medium">No open positions</span>
          <span className="text-sm text-muted-foreground mt-1">
            No positions in your {isPaper ? 'paper' : 'live'} Alpaca account
          </span>
        </div>
      )}

      {/* Positions Table */}
      {!loading && positions.length > 0 && (
        <>
          <div className="rounded-md border overflow-auto scrollbar-thin">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHeader field="symbol">Symbol</SortableHeader>
                  <TableHead>Bot</TableHead>
                  <TableHead>Side</TableHead>
                  <TableHead>Qty</TableHead>
                  <TableHead>Entry</TableHead>
                  <TableHead>Current</TableHead>
                  <TableHead className="text-right">Stop Loss</TableHead>
                  <TableHead className="text-right">Take Profit</TableHead>
                  <TableHead>TP/SL Proximity</TableHead>
                  <TableHead>Risk</TableHead>
                  <SortableHeader field="unrealized_pnl" className="text-right">P&L</SortableHeader>
                  <SortableHeader field="unrealized_pnl_pct" className="text-right">P&L %</SortableHeader>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedPositions.map((position, idx) => (
                  <TableRow key={`${position.symbol}-${position.side}-${position.avg_entry_price}-${idx}`}>
                    <TableCell className="font-medium">
                      <ClickableTicker symbol={position.symbol} />
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const ak = position.attribution_kind || '';
                        if (
                          position.is_external ||
                          ak === 'external_broker_order' ||
                          ak === 'manual_protect_track' ||
                          ak === 'user_bulk_import'
                        ) {
                          const extTitle =
                            ak === 'manual_protect_track'
                              ? 'Manually protected via Titan (e.g. protect-all) — not tied to an automated bot run.'
                              : ak === 'user_bulk_import'
                                ? 'Imported via admin/import-holdings — attribute manually if needed.'
                                : 'Opened outside Titan or confirmed non-Titan broker order id. Excluded from per-pattern bot stats.';
                          return (
                            <span
                              className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-rose-500/10 text-rose-600 border border-rose-500/20"
                              title={extTitle}
                            >
                              {ak === 'manual_protect_track'
                                ? 'Manual protect'
                                : ak === 'user_bulk_import'
                                  ? 'Bulk import'
                                  : 'External'}
                            </span>
                          );
                        }
                        // titan_order_untagged = Titan submitted this order but attribution
                        // ledger lookup hasn't resolved the bot yet. Show "resolving" state —
                        // never "Manual/External" for Titan-originated trades.
                        const PENDING_LABELS = new Set([
                          'titan_order_untagged',
                          'unlinked_import',
                        ]);
                        const PENDING_PATTERN_NAMES = new Set([
                          '[Titan Bot — attribution pending]',
                          '[Titan — pattern unresolved]',
                          '[Titan - pattern unresolved]',
                          '[Unlinked — not attributed to a bot]',
                          '[Synced from broker]',
                          '[Unknown bot — pre-tagging]',
                          '[Unknown bot - pre-tagging]',
                          '[Recovered — Titan]',
                          '[Recovered - Titan]',
                          '[Manual/Synced Position]',
                          '[Imported from broker]',
                        ]);
                        const isPending = PENDING_LABELS.has(ak) || PENDING_PATTERN_NAMES.has(position.pattern_name || '');
                        if (isPending) {
                          return (
                            <span
                              className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-amber-500/10 text-amber-700 border border-amber-500/20"
                              title="Titan-submitted order — bot attribution is being resolved from the attribution ledger. Check decision logs if this persists."
                            >
                              Titan (resolving…)
                            </span>
                          );
                        }
                        const name = position.pattern_name || '';
                        return (
                          <span
                            className="text-xs text-muted-foreground truncate max-w-[140px] inline-block"
                            title={name}
                          >
                            {name}
                          </span>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {(() => {
                          const posTags = (position.tags || {}) as Record<string, string>;
                          const isCash = posTags.coinbase_cash === 'usd' || position.asset_class === 'cash';
                          const label = isCash ? 'CASH' : position.side.toUpperCase();
                          const longish = isCash || position.side === 'long';
                          return (
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                                longish ? 'bg-emerald-500/10 text-emerald-700' : 'bg-rose-500/10 text-rose-700'
                              }`}
                              title={isCash ? 'USD cash balance (Coinbase) — not a margin short' : undefined}
                            >
                              {label}
                            </span>
                          );
                        })()}
                        {!equitiesOnly && position.asset_class && position.asset_class !== 'stocks' && position.asset_class !== 'cash' && (
                          <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium bg-blue-500/10 text-blue-600 uppercase">
                            {position.asset_class}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{position.quantity}</TableCell>
                    <TableCell>{formatCurrency(position.avg_entry_price)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {position.current_price > position.avg_entry_price ? (
                          <TrendingUp className="w-3 h-3 text-emerald-500" />
                        ) : (
                          <TrendingDown className="w-3 h-3 text-rose-500" />
                        )}
                        {formatCurrency(position.current_price)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {editingCell?.symbol === position.symbol && editingCell?.field === 'stop_loss' ? (
                        <div className="flex items-center gap-1 justify-end">
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
                            <Input
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyDown={handleEditKeyDown}
                              onBlur={() => { if (!savingEdit) cancelEditing(); }}
                              autoFocus
                              type="number"
                              step="0.01"
                              min="0.01"
                              className="w-24 h-7 text-sm pl-5"
                              disabled={savingEdit}
                            />
                          </div>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={saveEdit} disabled={savingEdit}>
                            {savingEdit ? <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current" /> : <Check className="w-3 h-3" />}
                          </Button>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={cancelEditing} disabled={savingEdit}>
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <div
                          onClick={() => startEditing(position.symbol, 'stop_loss', position.stop_loss_price)}
                          className="cursor-pointer flex items-center justify-end gap-1 rounded px-1 py-0.5 hover:bg-muted group"
                          title="Click to edit stop loss"
                        >
                          {position.stop_loss_price ? (
                            <span className="text-sm">{formatCurrency(position.stop_loss_price)}</span>
                          ) : (
                            <span className="text-muted-foreground text-sm">--</span>
                          )}
                          <Edit2 className="w-3 h-3 opacity-0 group-hover:opacity-50 transition-opacity" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {editingCell?.symbol === position.symbol && editingCell?.field === 'take_profit' ? (
                        <div className="flex items-center gap-1 justify-end">
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
                            <Input
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyDown={handleEditKeyDown}
                              onBlur={() => { if (!savingEdit) cancelEditing(); }}
                              autoFocus
                              type="number"
                              step="0.01"
                              min="0.01"
                              className="w-24 h-7 text-sm pl-5"
                              disabled={savingEdit}
                            />
                          </div>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={saveEdit} disabled={savingEdit}>
                            {savingEdit ? <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current" /> : <Check className="w-3 h-3" />}
                          </Button>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={cancelEditing} disabled={savingEdit}>
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <div
                          onClick={() => startEditing(position.symbol, 'take_profit', position.take_profit_price)}
                          className="cursor-pointer flex items-center justify-end gap-1 rounded px-1 py-0.5 hover:bg-muted group"
                          title="Click to edit take profit"
                        >
                          {position.take_profit_price ? (
                            <span className="text-sm">{formatCurrency(position.take_profit_price)}</span>
                          ) : (
                            <span className="text-muted-foreground text-sm">--</span>
                          )}
                          <Edit2 className="w-3 h-3 opacity-0 group-hover:opacity-50 transition-opacity" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <TPSLGauge
                        entryPrice={position.avg_entry_price}
                        currentPrice={position.current_price}
                        stopLoss={position.stop_loss_price}
                        takeProfit={position.take_profit_price}
                        side={
                          ((position.tags || {}) as Record<string, string>).coinbase_cash === 'usd' ||
                          position.asset_class === 'cash'
                            ? 'cash'
                            : position.side
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <RiskIndicator position={position} />
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={`font-medium ${position.unrealized_pnl >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                        {formatCurrency(position.unrealized_pnl)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={`font-medium ${position.unrealized_pnl_pct >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                        {formatPercent(position.unrealized_pnl_pct)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => liquidatePosition(position.symbol)}
                        disabled={liquidating === position.symbol}
                        className="bg-purple-600 hover:bg-purple-700 text-white border-purple-600 text-xs px-2 py-1 h-auto"
                        title="Liquidate position at market price"
                      >
                        {liquidating === position.symbol ? 'Selling...' : 'Liquidate'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {positions.length > 10 && totalPages > 1 && (
            <div className="flex items-center justify-between text-sm text-muted-foreground pt-2">
              <div className="flex items-center gap-2">
                <span>Positions per page:</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                  className="bg-transparent border rounded px-1 py-0.5 text-sm"
                >
                  {[10, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span>{page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, sortedPositions.length)} of {sortedPositions.length}</span>
                <Button variant="ghost" size="icon" className="h-8 w-8" disabled={page === 0} onClick={() => setPage(page - 1)}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="m15 18-6-6 6-6"/></svg>
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="m9 18 6-6-6-6"/></svg>
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Success Toast */}
      {successMessage && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-3 bg-emerald-600 text-white rounded-lg shadow-lg">
          <span className="text-sm">{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)} className="opacity-70 hover:opacity-100">&times;</button>
        </div>
      )}
    </div>
  );
};

export default PositionMonitor;
